// Delivery address lookup for the Wolt address form.
//
// Geocoding runs on our own backend (POST /woltDelivery/geocode) rather than in
// the browser: the Google key stays server-side, and web and mobile resolve an
// address through exactly the same path. Nothing here reads the device's own
// position — where the phone is is not necessarily where the food goes.
//
// Backend: heywaitr-staging/src/controller/woltDelivery.js -> geocode()

import axios from 'axios';
import configData from '../config.json';

// Google returns the street line already ordered for the address's own locale
// ("Nørrebrogade 12", not "12 Nørrebrogade"), so the formatted address is a
// better street source than stitching the parts back together ourselves.
const parseStreet = (result) => {
  const streetLine = (result.formattedAddress || '').split(',')[0]?.trim();
  if (streetLine) return streetLine;

  return [result.streetName, result.streetNumber].filter(Boolean).join(' ');
};

const toAddress = (result) => {
  // A street with no house number geocodes to the middle of that street. Filling
  // the fields from it is useful; sending that point to Wolt as the dropoff is
  // not — the courier would be routed to the street's centre. Without
  // coordinates the backend falls back to Wolt geocoding street/city/post_code.
  const isPrecise = !!result.streetNumber;

  return {
    street: parseStreet(result),
    zipcode: result.zipcode || '',
    city: result.city || '',
    lat: isPrecise && Number.isFinite(result.latitude) ? result.latitude : null,
    long: isPrecise && Number.isFinite(result.longitude) ? result.longitude : null,
  };
};

/**
 * Resolves the address the customer typed.
 *
 * Returns `{ status, address }`:
 *   'resolved'    — `address` is the lookup's version of it, with coordinates
 *                   when it is precise enough to route a courier to
 *   'not_found'   — no such address
 *   'unavailable' — the lookup could not be reached, which is our problem and
 *                   must not be treated as a bad address
 */
export const geocodeAddress = async ({ street, zipcode, city }) => {
  const address = [street, [zipcode, city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
    .trim();

  if (!address) return { status: 'not_found', address: null };

  try {
    const { data } = await axios.post(`${configData.SERVER_URL}/woltDelivery/geocode`, { address });

    return Number.isFinite(data?.latitude)
      ? { status: 'resolved', address: toAddress(data) }
      : { status: 'not_found', address: null };
  } catch (error) {
    // 404 is the endpoint's own "no such address"; anything else is a fault on
    // our side and the customer should not be told their address is wrong
    return error?.response?.status === 404
      ? { status: 'not_found', address: null }
      : { status: 'unavailable', address: null };
  }
};
